import { Directive, ElementRef, EventEmitter, HostBinding, HostListener, Input, OnDestroy, Output, Renderer2 } from '@angular/core';
import { LiveAnnouncer } from '@angular/cdk/a11y';
import { TranslateService } from '@ngx-translate/core';
import { REORDER_FOCUS_SETTLE_DELAY_MS, REORDER_LIVE_REGION_DELAY_MS } from './accessible-drop-list.constants';

export interface ItemChangeEvent {
	currentIndex: number;
	newIndex: number;
}

export type AccessibleDropListItemId = number | string;

export interface ReorderCancelEvent {
	currentIndex: number;
	itemId: AccessibleDropListItemId;
}

@Directive({
	selector: '[accessibleDropList]'
})
export class AccessibleDropListDirective implements OnDestroy {
	@Input() public count: number;
	@Input() public currentIndex: number;
	@Input() public itemId: AccessibleDropListItemId;
	@Input() public name: string;
	@Input() public accessibleDropListDisabled = false;
	@Input() public accessibleDropListUseLiveRegionFallback = false;
	@Output() public activeItemChange: EventEmitter<ItemChangeEvent> = new EventEmitter<ItemChangeEvent>();
	@Output() public cancelReorder: EventEmitter<ReorderCancelEvent> = new EventEmitter<ReorderCancelEvent>();
	@Output() public finalizePosition: EventEmitter<void> = new EventEmitter<void>();
	@HostBinding('attr.data-accessible-drop-list-item-id')
	public get dataAccessibleDropListItemId (): string {
		return this.itemId?.toString() ?? '';
	}
	private liveRegionElement: HTMLElement | null = null;
	private liveRegionTimeout: ReturnType<typeof setTimeout> | null = null;
	private focusOutTimeout: ReturnType<typeof setTimeout> | null = null;
	private keyboardClickTimeout: ReturnType<typeof setTimeout> | null = null;
	private suppressNextKeyboardClick: boolean = false;

	constructor (
		private elementRef: ElementRef<HTMLElement>,
		private liveAnnouncer: LiveAnnouncer,
		private renderer: Renderer2,
		private translate: TranslateService
	) {}

	public ngOnDestroy (): void {
		if (this.liveRegionTimeout != null) {
			clearTimeout(this.liveRegionTimeout);
			this.liveRegionTimeout = null;
		}

		if (this.focusOutTimeout != null) {
			clearTimeout(this.focusOutTimeout);
			this.focusOutTimeout = null;
		}

		if (this.keyboardClickTimeout != null) {
			clearTimeout(this.keyboardClickTimeout);
			this.keyboardClickTimeout = null;
		}

		if (this.liveRegionElement != null) {
			this.renderer.removeChild(this.elementRef.nativeElement, this.liveRegionElement);
			this.liveRegionElement = null;
		}
	}

	@HostListener('keydown', ['$event'])
	public handleKeyDown (event: KeyboardEvent) {
		if (this.accessibleDropListDisabled) {
			return;
		}
		const listItem = event.currentTarget as HTMLElement;
		const isSelectKey = this.isSelectKey(event);
		const isArrowDownKey = this.isArrowDownKey(event);
		const isArrowUpKey = this.isArrowUpKey(event);
		const isArrowMoveKey = isArrowDownKey || isArrowUpKey;
		const isTabKey = this.isTabKey(event);
		const isEscapeKey = this.isEscapeKey(event);
		const isTabMoveKey = isTabKey && listItem.classList.contains('selected');
		const isMoveKey = isArrowMoveKey || isTabMoveKey;
		const isNestedInteractiveElem = this.isNestedInteractiveElem(event.target, listItem);

		if ((isSelectKey || isMoveKey || isEscapeKey) && !isNestedInteractiveElem) {
			event.preventDefault();
		}

		if (isSelectKey) {
			this.suppressKeyboardClick();
			this.toggleSelection(listItem);
		}

		if (listItem.classList.contains('selected')) {
			if (isMoveKey) {
				let newIndex = this.currentIndex;
				if (isArrowDownKey || (isTabKey && !event.shiftKey)) {
					newIndex += 1;
				} else {
					newIndex -= 1;
				}
				if (newIndex < 0 || newIndex >= this.count) {
					return;
				}
				const message = this.translate.instant('aria-ng-sortable_moved', {
					name: this.name,
					position: newIndex + 1,
					count: this.count
				});
				this.announce(message);
				this.activeItemChange.emit({ currentIndex: this.currentIndex, newIndex });
			} else if (isEscapeKey) {
				event.stopPropagation();
				this.clearSelection(listItem);
				const message = this.translate.instant('aria-ng-sortable_cancelled', {
					name: this.name
				});
				this.announce(message);
				this.cancelReorder.emit({currentIndex: this.currentIndex, itemId: this.itemId});
			}
		}
	}

	@HostListener('click', ['$event'])
	public handleClick (event: MouseEvent) {
		const listItem = this.elementRef.nativeElement;
		const isNestedInteractiveElem = this.isNestedInteractiveElem(event.target, listItem);
		if (this.accessibleDropListDisabled || isNestedInteractiveElem) {
			return;
		}

		if (this.suppressNextKeyboardClick) {
			this.clearKeyboardClickSuppression();
			return;
		}

		event.preventDefault();
		this.toggleSelection(listItem);
	}

	@HostListener('focusout', ['$event'])
	public handleFocusOut (_event: FocusEvent) {
		if (this.accessibleDropListDisabled) {
			return;
		}

		if (this.focusOutTimeout != null) {
			clearTimeout(this.focusOutTimeout);
		}

		this.focusOutTimeout = setTimeout(() => {
			const listItem = this.elementRef.nativeElement;
			const activeElement = document.activeElement as HTMLElement | null;
			const focusReturnedToSameItem = activeElement?.getAttribute('data-accessible-drop-list-item-id') === this.itemId?.toString();
			if (!listItem.isConnected || !listItem.classList.contains('selected') || activeElement === listItem || focusReturnedToSameItem) {
				this.focusOutTimeout = null;
				return;
			}

			this.clearSelection(listItem);
			const message = this.translate.instant('aria-ng-sortable_cancelled', {
				name: this.name
			});
			this.announce(message);
			this.cancelReorder.emit({currentIndex: this.currentIndex, itemId: this.itemId});
			this.focusOutTimeout = null;
		}, REORDER_FOCUS_SETTLE_DELAY_MS);
	}

	private announce (message: string, politeness: 'polite' | 'assertive' = 'polite'): void {
		if (!this.accessibleDropListUseLiveRegionFallback) {
			this.liveAnnouncer.announce(message, politeness);
			return;
		}

		const liveRegion = this.getOrCreateLiveRegion();
		this.renderer.setAttribute(liveRegion, 'role', politeness === 'assertive' ? 'alert' : 'status');
		this.renderer.setAttribute(liveRegion, 'aria-live', politeness);
		this.renderer.setProperty(liveRegion, 'textContent', '');

		if (this.liveRegionTimeout != null) {
			clearTimeout(this.liveRegionTimeout);
		}

		this.liveRegionTimeout = setTimeout(() => {
			this.renderer.setProperty(liveRegion, 'textContent', message);
			this.liveRegionTimeout = null;
		}, REORDER_LIVE_REGION_DELAY_MS);
	}

	private clearSelection (listItem: HTMLElement): void {
		listItem.classList.remove('selected');
		listItem.setAttribute('aria-grabbed', 'false');
	}

	private isNestedInteractiveElem (target: EventTarget | null, listItem: HTMLElement): boolean {
		if (!(target instanceof HTMLElement) || target === listItem) {
			return false;
		}

		const nestedInteractiveElem = target.closest('button, input[type="checkbox"]');
		return nestedInteractiveElem != null && nestedInteractiveElem !== listItem;
	}

	private isSelectKey (event: KeyboardEvent): boolean {
		return event.code === 'Enter' ||
			event.code === 'Space' ||
			event.key === 'Enter' ||
			event.key === ' ' ||
			event.key === 'Spacebar';
	}

	private isArrowDownKey (event: KeyboardEvent): boolean {
		return event.code === 'ArrowDown' || event.key === 'ArrowDown';
	}

	private isArrowUpKey (event: KeyboardEvent): boolean {
		return event.code === 'ArrowUp' || event.key === 'ArrowUp';
	}

	private isTabKey (event: KeyboardEvent): boolean {
		return event.code === 'Tab' || event.key === 'Tab';
	}

	private isEscapeKey (event: KeyboardEvent): boolean {
		return event.code === 'Escape' || event.key === 'Escape' || event.key === 'Esc';
	}

	private toggleSelection (listItem: HTMLElement): void {
		listItem.classList.toggle('selected');
		listItem.setAttribute('aria-grabbed', listItem.classList.contains('selected').toString());

		if (listItem.classList.contains('selected')) {
			const message = this.translate.instant('aria-ng-sortable_grabbed', {
				name: this.name,
				position: this.currentIndex + 1,
				count: this.count
			});
			this.announce(message);
			return;
		}

		const message = this.translate.instant('aria-ng-sortable_dropped', {
			name: this.name,
			position: this.currentIndex + 1,
			count: this.count
		});
		this.announce(message);
		this.finalizePosition.emit();
	}

	private suppressKeyboardClick (): void {
		this.suppressNextKeyboardClick = true;

		if (this.keyboardClickTimeout != null) {
			clearTimeout(this.keyboardClickTimeout);
		}

		this.keyboardClickTimeout = setTimeout(() => {
			this.clearKeyboardClickSuppression();
		}, 0);
	}

	private clearKeyboardClickSuppression (): void {
		if (this.keyboardClickTimeout != null) {
			clearTimeout(this.keyboardClickTimeout);
			this.keyboardClickTimeout = null;
		}

		this.suppressNextKeyboardClick = false;
	}

	private getOrCreateLiveRegion (): HTMLElement {
		if (this.liveRegionElement != null) {
			return this.liveRegionElement;
		}

		const liveRegion = this.renderer.createElement('span') as HTMLElement;
		this.renderer.setAttribute(liveRegion, 'role', 'status');
		this.renderer.setAttribute(liveRegion, 'aria-live', 'polite');
		this.renderer.setAttribute(liveRegion, 'aria-atomic', 'true');
		this.renderer.setStyle(liveRegion, 'position', 'absolute');
		this.renderer.setStyle(liveRegion, 'width', '1px');
		this.renderer.setStyle(liveRegion, 'height', '1px');
		this.renderer.setStyle(liveRegion, 'padding', '0');
		this.renderer.setStyle(liveRegion, 'margin', '-1px');
		this.renderer.setStyle(liveRegion, 'overflow', 'hidden');
		this.renderer.setStyle(liveRegion, 'clip', 'rect(0, 0, 0, 0)');
		this.renderer.setStyle(liveRegion, 'white-space', 'nowrap');
		this.renderer.setStyle(liveRegion, 'border', '0');
		this.renderer.appendChild(this.elementRef.nativeElement, liveRegion);
		this.liveRegionElement = liveRegion;

		return liveRegion;
	}
}
